import React, { useState } from "react";
import { useDispatch } from "react-redux";
import { useTranslation } from "react-i18next";

import {
  Form,
  Input,
  Button,
  Checkbox,
  Image,
  Typography,
  Spin,
  message,
  Alert,
} from "antd";
import { UserOutlined, LockOutlined } from "@ant-design/icons";

import { loginController } from "../../controllers/authentication.controller";

import { logged } from "../../redux/actions";

import "../../index.css";
import "../../App.css";

const { Title, Text } = Typography;

function LoginForm() {
  // Hook for translation
  const { t } = useTranslation();

  // Instantiate useDispatch
  const dispatch = useDispatch();

  // Initialize local states
  const [isLoggingIn, setLoggingIn] = useState(false);
  const [alertMessage, setAlertMessage] = useState({ show: false, msg: "" });
  const [isInvalidEmail, setInvalidEmail] = useState(false);
  const [isInvalidPassword, setInvalidPassword] = useState(false);

  // Set function for setting states to error
  const setError = ( errorCode = 423 ) => {
    // Enable the login form again to allow retry to log in
    setLoggingIn(false);

    // Set inputs to invalid
    setInvalidEmail(true);
    setInvalidPassword(true);

    // Set alert message for incorrect login
    setAlertMessage({ show: true, msg: errorCode!==423? t("login.login_alert") : t("login.login_account_locked") });
    message.error(errorCode!==423? t("login.login_alert") : t("login.login_account_locked"));
  };

  // Set function to be triggered on form submit
  const onFinish = (credentials) => {
    // Disable the login form while logging in to backend
    setLoggingIn(true);

    // Call the login controller to initiate log in process
    loginController(credentials)
      .then((res) => {
        if (res) {
          // Set inputs to valid
          setInvalidEmail(false);
          setInvalidPassword(false);

          // Remove alert message
          setAlertMessage({ show: false, msg: "" });

          // Get user profile
          // This will be changed to get real user jobTitle
          let userProfile = {
            id: res.id,
            name: res.fullName,
            role : res.role,
            permissions: res.permissions,
          };

          // Update user profile
          dispatch(logged.profile(userProfile));

          // Show a login success message
          // Change isAuthenticated to true after message
          message
            .success({
              content: t("login.login_successful"),
            })
            .then(() => dispatch(logged.login()));
        } else {
          console.log(res)
          // Set the errors
          setError();
        }
      })
      .catch((err) => {
        if(err.response!==undefined){
          if(err.response.status!==undefined){
            setError(err.response.status)
          }
        }
        // Set the errors
      });
  };

  return (
    <div className="login-container">
      <div className="login-left-panel">
        <div className="logo-section">
          <Image
            src="/5R_Logo.png"
            preview={false}
            className="company-logo"
          />
          <Text className="company-slogan">
            {t("global.slogan")}
          </Text>
        </div>
      </div>
      
      <div className="login-right-panel">
        <Spin spinning={isLoggingIn} wrapperClassName="w-full">
          <div className="login-form-container">
            <Title level={2} className="login-title">
              {t("login.log_in")}
            </Title>
            
            {alertMessage.show ? (
              <Alert
                message={alertMessage.msg}
                type="error"
                showIcon
                className="w-full mb-5"
              />
            ) : null}
            
            <Form
              name="normal_login"
              layout="vertical"
              className="login-form"
              initialValues={{
                remember: true,
              }}
              onFinish={onFinish}
            >
              <Form.Item
                name="email"
                label={t("login.email")}
                validateStatus={isInvalidEmail ? "error" : null}
                rules={[
                  {
                    type: "email",
                    message: t("login.invalid_email"),
                  },
                  {
                    required: true,
                    message: t("login.missing_email"),
                  },
                ]}
              >
                <Input
                  prefix={<UserOutlined className="site-form-item-icon" />}
                  placeholder={t("login.enter_email")}
                />
              </Form.Item>
              
              <Form.Item
                name="password"
                label={t("login.password")}
                validateStatus={isInvalidPassword ? "error" : null}
                rules={[
                  {
                    required: true,
                    message: t("login.missing_password"),
                  },
                ]}
              >
                <Input.Password
                  prefix={<LockOutlined className="site-form-item-icon" />}
                  placeholder={t("login.enter_password")}
                />
              </Form.Item>
              
              <Form.Item>
                <Form.Item name="remember" valuePropName="checked" noStyle>
                  <Checkbox>
                    {t("login.remember_me")}
                  </Checkbox>
                </Form.Item>
              </Form.Item>
              
              <Form.Item>
                <Button 
                  type="primary" 
                  htmlType="submit" 
                  className="login-button"
                  block
                >
                  {t("login.log_in")}
                </Button>
              </Form.Item>
            </Form>
          </div>
        </Spin>
      </div>
    </div>
  );
}

export default LoginForm;
